import { CheckCircle, Circle } from "lucide-react";
import { cn } from "@/lib/utils";

interface Step {
  label: string;
  completed: boolean;
  active: boolean;
  timestamp?: string;
}

interface LifecycleTrackerProps {
  steps: Step[];
}

export function LifecycleTracker({ steps }: LifecycleTrackerProps) {
  return (
    <div className="flex w-full overflow-x-auto py-3 px-1">
      {/* Icons row + connectors — fixed height, perfectly aligned */}
      <div className="flex items-center w-full">
        {steps.map((step, i) => (
          <div key={step.label} className={cn("flex items-center", i < steps.length - 1 ? "flex-1" : "")}>
            <div className="flex flex-col items-center" style={{ width: 72 }}>
              {/* Icon — fixed position */}
              <div
                className={cn(
                  "flex items-center justify-center h-7 w-7 rounded-full border-2 transition-all shrink-0",
                  step.completed
                    ? "bg-primary border-primary text-primary-foreground"
                    : step.active
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-muted-foreground/30 bg-muted text-muted-foreground/50"
                )}
              >
                {step.completed ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <Circle className="h-3 w-3" />
                )}
              </div>
              {/* Label — fixed height container, text never shifts icons */}
              <div className="h-8 flex items-start justify-center mt-1">
                <span
                  className={cn(
                    "text-[10px] font-medium text-center leading-tight",
                    step.completed
                      ? "text-primary"
                      : step.active
                        ? "text-foreground font-semibold"
                        : "text-muted-foreground/60"
                  )}
                  style={{ maxWidth: 72, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}
                >
                  {step.label}
                </span>
              </div>
              {/* Timestamp */}
              {step.timestamp && (
                <span className="text-[9px] text-muted-foreground/50 -mt-1">
                  {new Date(step.timestamp).toLocaleDateString()}
                </span>
              )}
            </div>
            {/* Connector line — always vertically centered with icons */}
            {i < steps.length - 1 && (
              <div
                className={cn(
                  "h-0.5 flex-1 mx-0.5 rounded-full min-w-[8px]",
                  step.completed ? "bg-primary" : "bg-muted-foreground/20"
                )}
                style={{ marginBottom: "auto", marginTop: 12 }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
